import { LayoutService } from '@/layout/service/layout.service';
import { AccountDbQueryService } from '@account';
import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, AuthUser, CoreModule, IMessage, ImpersonationService, MessageBusService, MessageTypeEnum, NotificationService, ProfileEditRequestDialogComponent, UtilDialogService, ProxDialogServiceProvider } from '@core';
import { ToastrService } from 'ngx-toastr';
import { HasChangesService } from '@data';
import { GravatarQuickEditorCore } from '@gravatar-com/quick-editor';
import { ProximityRightEnum } from '@models';
import { sha256 } from 'js-sha256';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { AppBreadcrumb } from './app.breadcrumb';
import { NotificationsDialogComponent, NotificationsDialogResult } from 'libs/account-user/src/lib/notifications-dialog.component';

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [CoreModule, CommonModule, StyleClassModule, AppBreadcrumb, InputTextModule, ButtonModule, IconFieldModule, InputIconModule, RippleModule, BadgeModule, OverlayBadgeModule, AvatarModule],
    providers: [ProxDialogServiceProvider],
    templateUrl: 'app.topbar.html',
})
export class AppTopbar {
    layoutService = inject(LayoutService);
    authService = inject(AuthService);
    impersonationService = inject(ImpersonationService);
    dbQueryService = inject(AccountDbQueryService);
    messageBusService = inject(MessageBusService);
    hasChangesService = inject(HasChangesService);
    utilDialogService = inject(UtilDialogService);
    pngDialogService = inject(DialogService);
    router = inject(Router);
    notificationService = inject(NotificationService);
    toastr = inject(ToastrService);

    newNotificationsCount = 0;
    authUser?: AuthUser;
    intervalHandle!: number;
    f = 'mp';
    emHash = '';
    gravatarLink = '';
    gravatarEditor?: GravatarQuickEditorCore;

    isDarkTheme = computed(() => this.layoutService.isDarkTheme());

    @ViewChild('menubutton') menuButton!: ElementRef;

    async ngOnInit(): Promise<void> {
        const authUser = await this.authService.getLoggedInUser();
        this.authUser = authUser?.actingAsUser ?? authUser;

        if (this.authUser?.email) {
            const c = sha256(this.authUser?.email);
            this.emHash = c;
            this.gravatarLink = "https://www.gravatar.com/avatar/" + this.emHash + "?d=identicon"
        }

        this.messageBusService.messages.subscribe(this.checkMessageBus.bind(this));

        // check notifications immediately, then check every 5 minutes.
        this.intervalHandle = window.setInterval(this.checkInAppNotifications, 1000 * 60 * 5);

        this.gravatarEditor = new GravatarQuickEditorCore({
            email: this.authUser?.email,
            scope: ['avatars'],
            onProfileUpdated: () => {
                // when profile is updated, update img url with timestamp so it reloads
                this.gravatarLink = this.gravatarLink.replace(/d=identicon.*?$/, 'd=identicon&ts=' + new Date().getTime());
            },
            // onOpened: () => {
            //     console.log('Editor opened!');
            // },
        });
    }

    ngOnDestroy() {
        clearInterval(this.intervalHandle);
    }

    private async checkMessageBus(message: IMessage) {
        if (message.id == MessageTypeEnum.Noop) {
            this.checkInAppNotifications();
        } else if (message.id == MessageTypeEnum.Login) {
            // do something maybe
        } else if (message.id == MessageTypeEnum.Notification) {
            await this.checkInAppNotifications();
        }
        return null;
    }

    private async checkInAppNotifications() {
        if (this.authUser == null) { return; }
        this.newNotificationsCount = await this.dbQueryService.getInAppNotificationsCount(this.authUser.id, true);
    }

    private canUserUpdate() {
        return this.authUser?.hasRight(ProximityRightEnum.CanEditOwnData, ProximityRightEnum.CanManageAccountOrganization, ProximityRightEnum.CanManageSupplierAdministration);
    }

    private getUserUpdateUrl() {
        if (!this.authUser) { return ''; }
        const user = this.authUser;
        if (user.accountAdminId) {
            return `/account/accounts/${user.accountId}/manage/account-administrators/${user.accountAdminId}`;
        } else if (user.accountUserId) {
            return `/account/accounts/${user.accountId}/manage/account-users/${user.accountAdminId}`;
        } else if (user.supplierAdminId) {
            return `/supplier/suppliers/${user.supplierId}/manage/supplier-administrators/${user.supplierAdminId}`;
        } else {
            return '';
        }
    }

    get userDisplayName(): string {
        return this.authService.userDisplayName;
    }

    get companyName() {
        //return this.authService.getUser()?.companyName;
        return '';
    }

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    showRightMenu() {
        this.layoutService.toggleRightMenu();
    }

    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }

    toggleSearchBar() {
        this.layoutService.layoutState.update((value) => ({ ...value, searchBarActive: !value.searchBarActive }));
    }

    get hasChanges() {
        return this.hasChangesService.hasChanges;
    }

    get saveChangesTooltip() {
        return this.hasChanges ? 'Save changes first' : null;
    }

    setGravatar() {
        this.gravatarEditor?.open();
    }

    async showProfile() {
        if (this.authUser) {
            const url = this.canUserUpdate() ? this.getUserUpdateUrl() : undefined;
            const canRequestEdit = !!this.authUser.accountUserId && !!this.authUser.accountId;
            const result = await this.utilDialogService.userFrmDialog(this.authUser, 'User Profile', url, canRequestEdit);
            if (result === 'requestEdit') {
                await this.requestProfileEdit();
            }
        }
    }

    private async requestProfileEdit() {
        const user = this.authUser;
        if (!user || !user.accountUserId || !user.accountId) return;

        // Open the profile edit request dialog
        const ref = this.pngDialogService.open(ProfileEditRequestDialogComponent, {
            data: { user },
            header: 'Request Profile Update',
            modal: true,
            width: '500px',
            contentStyle: { overflow: 'auto' },
            focusOnShow: false,
        });

        const message: string | null = await new Promise(resolve => {
            ref.onClose.subscribe(result => resolve(result ?? null));
        });

        if (!message) return;

        try {
            const response = await this.utilDialogService.requestProfileEdit(user.accountUserId, message);
            if (response.success) {
                this.toastr.success(response.message, 'Request Sent');
            } else {
                this.toastr.warning(response.message, 'Request Failed');
            }
        } catch {
            this.toastr.error('An error occurred while sending your request.', 'Error');
        }
    }

    changePassword() {
        if (!this.hasChanges) {
            this.router.navigate(['/auth/change-password']);
        }
    }

    goToLogin() {
        this.authService.redirectIfNotLoggedIn();
    }

    logout() {
        if (!this.hasChanges) {
            this.authService.logout();
        }
    }

    canImpersonate() {
        return this.impersonationService.canImpersonateAccountUser() || this.impersonationService.canImpersonateSupplierAdmin();
    }

    impersonate() {
        if (!this.hasChanges) {
            this.impersonationService.openDialog();
        }
    }

    canStopImpersonation() {
        return !!this.authService.getUser()?.actingAsUser;
    }
    stopImpersonation() {
        if (!this.hasChanges) {
            return this.authService.stopImpersonation();
        }
    }

    // TEMPORARY — POC test methods for notification banner (remove after verification)
    private testNotifCounter = 0;
    private severities = ['success', 'info', 'warn', 'error'];
    testNotification() {
        const severity = this.severities[this.testNotifCounter % this.severities.length];
        this.testNotifCounter++;
        const autoClose = severity === 'success' ? 5000 : undefined;
        this.notificationService.add(severity, `Test ${severity} notification`, `This is a sample ${severity} message (#${this.testNotifCounter}).`, autoClose);
    }

    showNotifications() {
        if (!this.authUser) return;

        const ref = NotificationsDialogComponent.openDialog(this.pngDialogService, {
            proximityUserId: this.authUser.id,
            userName: this.authUser.fullName || this.userDisplayName,
        });

        ref.onClose.subscribe((result: NotificationsDialogResult) => {
            if (result?.hasChanges) {
                // Refresh the notification count after changes were saved
                this.checkInAppNotifications();
            }
        });
    }
}
